import type { Coords, ProductionResult, RoofModel, PanelLayout } from '../types';

// ============================================================
// PRODUCTION ESTIMATE — NREL PVWatts API
// Free, CORS-enabled, NREL-validated
//
// getProductionEstimateByFace() is the primary path: it groups
// the placed panels by roof face and calls PVWatts once per
// distinct (tilt, azimuth) bucket, so a north-facing face is no
// longer priced like a due-south 25° one. Buckets round tilt and
// azimuth to 5° to merge near-identical faces and cap API calls
// (typical roof: 1-3 calls).
//
// Falls back to the single-call estimate, then to a latitude-
// based offline estimate.
// ============================================================

const isOffline = typeof window !== 'undefined' && window.location.protocol === 'file:';

// PVWatts default-style losses include a generic ~3% shading component.
// When a face has real shade data (measured or modeled), we remove that
// generic component and apply the face's own solar access instead —
// otherwise shading would be counted twice.
const BASE_LOSSES = 14;
const LOSSES_EXCL_SHADING = 11;

function getApiKey(): string {
  return (import.meta.env.VITE_NREL_PVWATTS_API_KEY as string) || 'DEMO_KEY';
}

async function callPvwatts(
  coords: Coords,
  systemKW: number,
  tilt: number,
  azimuth: number,
  losses: number
): Promise<{ annual: number; monthly: number[] } | null> {
  const url =
    `https://developer.nrel.gov/api/pvwatts/v8.json?api_key=${getApiKey()}` +
    `&lat=${coords.lat}&lon=${coords.lon}&system_capacity=${systemKW.toFixed(3)}` +
    `&tilt=${tilt.toFixed(1)}&azimuth=${azimuth.toFixed(1)}&losses=${losses}` +
    `&array_type=1&module_type=1&dataset=nsrdb`;
  const resp = await fetch(url);
  if (!resp.ok) return null;
  const data = await resp.json();
  if (!data?.outputs?.ac_annual) return null;
  return {
    annual: data.outputs.ac_annual as number,
    monthly: ((data.outputs.ac_monthly ?? []) as number[]).slice(0, 12),
  };
}

function offlineEstimate(coords: Coords, systemKW: number): ProductionResult {
  const latFactor = Math.max(0.14, 0.22 - (Math.abs(coords.lat) - 25) * 0.003);
  const annualKWh = Math.round(systemKW * 8760 * latFactor);
  const monthlyWeights = [0.06, 0.07, 0.08, 0.09, 0.10, 0.11, 0.11, 0.10, 0.09, 0.08, 0.06, 0.05];
  const monthly = monthlyWeights.map(w => Math.round(annualKWh * w));
  return {
    annual_kwh: annualKWh,
    monthly_kwh: monthly,
    system_losses_pct: BASE_LOSSES,
    offset_pct: null,
    source: 'estimated',
  };
}

/**
 * Legacy single-call estimate (system-level tilt=25/azimuth=180).
 * Retained as the fallback when face data is unavailable or a
 * face-aware call fails (e.g., DEMO_KEY rate limiting).
 */
export async function getProductionEstimate(coords: Coords, systemKW: number): Promise<ProductionResult> {
  if (systemKW < 0.05) systemKW = 0.05;

  if (!isOffline && !coords.offline) {
    try {
      const res = await callPvwatts(coords, systemKW, 25, 180, BASE_LOSSES);
      if (res) {
        return {
          annual_kwh: Math.round(res.annual),
          monthly_kwh: res.monthly.map(v => Math.round(v)),
          system_losses_pct: BASE_LOSSES,
          offset_pct: null,
          source: 'pvwatts',
        };
      }
    } catch {
      // fall through to estimate
    }
  }

  return offlineEstimate(coords, systemKW);
}

interface FaceBucket {
  tilt: number;
  azimuth: number;
  kw: number;
  weightedAccess: number; // kW-weighted solar access factor (0-1)
  losses: number;
}

/**
 * Face-aware production estimate: one PVWatts call per distinct
 * (tilt, azimuth, losses) bucket, weighted by the kW placed on
 * matching faces. Faces with real shade data additionally derate
 * by their measured solar access.
 */
export async function getProductionEstimateByFace(
  coords: Coords,
  roofModel: RoofModel,
  panelLayout: PanelLayout
): Promise<ProductionResult> {
  const systemKW = Math.max(0.05, panelLayout.total_system_kw);
  if (isOffline || coords.offline) return offlineEstimate(coords, systemKW);
  if (panelLayout.panels.length === 0) return getProductionEstimate(coords, systemKW);

  const faceById = new Map(roofModel.faces.map(f => [f.id, f]));
  const buckets = new Map<string, FaceBucket>();

  for (const panel of panelLayout.panels) {
    const face = faceById.get(panel.face_id);
    const tilt = Math.min(90, Math.max(0, face?.pitch ?? 25));
    let azimuth = (face?.azimuth ?? 180) % 360;
    if (azimuth < 0) azimuth += 360;

    const tiltBucket = Math.round(tilt / 5) * 5;
    const azBucket = (Math.round(azimuth / 5) * 5) % 360;

    const hasRealShade = !!(face && face.shade_points && face.shade_points.length > 0);
    const accessFactor = hasRealShade ? face!.solar_access / 100 : 1;
    const losses = hasRealShade ? LOSSES_EXCL_SHADING : BASE_LOSSES;

    const key = `${tiltBucket}_${azBucket}_${losses}`;
    const kwPanel = panel.wattage / 1000;
    const bucket = buckets.get(key) ?? {
      tilt: tiltBucket,
      azimuth: azBucket,
      kw: 0,
      weightedAccess: 0,
      losses,
    };
    bucket.kw += kwPanel;
    bucket.weightedAccess += accessFactor * kwPanel;
    buckets.set(key, bucket);
  }

  try {
    let annual = 0;
    const monthly = new Array(12).fill(0) as number[];

    for (const bucket of buckets.values()) {
      const res = await callPvwatts(coords, Math.max(0.05, bucket.kw), bucket.tilt, bucket.azimuth, bucket.losses);
      if (!res) throw new Error('PVWatts call failed');
      const accessFactor = bucket.kw > 0 ? bucket.weightedAccess / bucket.kw : 1;
      annual += res.annual * accessFactor;
      res.monthly.forEach((v, i) => {
        monthly[i] += v * accessFactor;
      });
    }

    return {
      annual_kwh: Math.round(annual),
      monthly_kwh: monthly.map(v => Math.round(v)),
      system_losses_pct: BASE_LOSSES,
      offset_pct: null,
      source: 'pvwatts',
    };
  } catch {
    return getProductionEstimate(coords, systemKW);
  }
}
